// frontend/src/components/ProjectView/AudienceManagementSection.js
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { collection, addDoc, deleteDoc, doc, updateDoc, serverTimestamp, getDocs, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { AgentApiClient } from '@modules/content-agent/services/AgentApiClient';
import { useNotification } from '../../contexts/NotificationContext';
// Legacy TaskContext — kept for setCurrentProjectId only
import { useTaskContext } from '../../contexts/TaskContext';
import { getProjectAudiencesPath, getProjectAudienceActorsPath, getProjectActorSubcollectionPath } from '../../utils/collectionPaths';
import { WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import {
  buildActorGenerationAudienceRequests,
  getAudienceAcquisitionChannelsForDisplay,
} from './audienceActorRequestUtils';

// MUI Imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { getProviderForModel } from '@modules/ui-components';
import {
  AvailableModelSelector,
  PROJECT_DEFAULT_AI_MODEL,
  useProjectDefaultAIModel,
} from '@modules/ai-settings';
import AddIcon from '@mui/icons-material/Add';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'; // For Delete Selected
import GroupIcon from '@mui/icons-material/Group'; // Added import
import Alert from '@mui/material/Alert'; // Added import
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TableSortLabel from '@mui/material/TableSortLabel';
import CloseIcon from "@mui/icons-material/Close";

// Helper function for formatting Firestore Timestamps or JS Dates
const formatTimestamp = (timestampInput) => {
  if (!timestampInput) return 'N/A';
  const timestamp = timestampInput.toDate ? timestampInput.toDate() : new Date(timestampInput);
  return timestamp.toLocaleDateString() + ' ' + timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const AudienceAcquisitionChannelsCell = ({ audience }) => {
  const channels = getAudienceAcquisitionChannelsForDisplay(audience);

  if (channels.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled">
        Not set
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {channels.map((channel) => (
        <Chip
          key={channel.key}
          label={`${channel.isPrimary ? 'Primary: ' : ''}${channel.label} ${channel.percentage}%`}
          size="small"
          color={channel.isPrimary ? 'primary' : 'default'}
          variant={channel.isPrimary ? 'filled' : 'outlined'}
          title={[
            `${channel.fit} acquisition fit`,
            channel.rationale,
          ].filter(Boolean).join(': ')}
          sx={{
            maxWidth: 220,
            height: 22,
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        />
      ))}
    </Box>
  );
};

AudienceAcquisitionChannelsCell.propTypes = {
  audience: PropTypes.object.isRequired,
};

/**
 * Recursively deletes an audience with all its actors and subcollections
 * @param {string} projectId - The project ID
 * @param {string} audienceId - The audience ID to delete
 * @returns {Promise<void>}
 */
const deleteAudienceWithActorsAndSubcollections = async (projectId, audienceId) => {
  try {
    // Get all actors in this audience
    const actorsCol = collection(db, getProjectAudienceActorsPath(projectId, audienceId));
    const actorsSnapshot = await getDocs(actorsCol);
    
    // Delete each actor and their subcollections
    for (const actorDoc of actorsSnapshot.docs) {
      const actorId = actorDoc.id;
      
      // Define the subcollections that actors have
      const subcollections = ['coreJobs', 'contexts', 'fears', 'bigJobs', 'subtasks', 'competitors', 'habits'];
      
      // Delete each subcollection
      for (const subcollectionName of subcollections) {
        const subcollectionRef = collection(db, getProjectActorSubcollectionPath(projectId, audienceId, actorId, subcollectionName));
        const subcollectionSnapshot = await getDocs(subcollectionRef);
        
        // Delete all documents in the subcollection
        for (const subDoc of subcollectionSnapshot.docs) {
          await deleteDoc(subDoc.ref);
        }
      }
      
      // Delete the actor document itself
      await deleteDoc(actorDoc.ref);
    }
    
    // Finally, delete the audience document
    await deleteDoc(doc(db, getProjectAudiencesPath(projectId), audienceId));
    
  } catch (error) {
    console.error(`Error deleting audience ${audienceId} with all its data:`, error);
    throw error;
  }
};

function AudienceManagementSection({ 
  project,
  projectId, 
  audiences, 
  onAudiencesChange, 
  onActorsCreated, 
  currentUser,
  fetchProjectData,
  clearProjectTaskStatus,
  selectedAudienceIds: controlledSelectedAudienceIds,
  setSelectedAudienceIds: setControlledSelectedAudienceIds,
  tasks,
  searchParams,
  setSearchParams
}) {
  // Use controlled state if provided, otherwise fallback to internal state
  const [internalSelectedAudienceIds, internalSetSelectedAudienceIds] = useState(new Set());
  const selectedAudienceIds = controlledSelectedAudienceIds !== undefined ? controlledSelectedAudienceIds : internalSelectedAudienceIds;
  const setSelectedAudienceIds = setControlledSelectedAudienceIds !== undefined ? setControlledSelectedAudienceIds : internalSetSelectedAudienceIds;
  // const [isProcessing, setIsProcessing] = useState(false); // Replaced by project.taskStatus for actor creation
  const [isFormProcessing, setIsFormProcessing] = useState(false); // For dialog save/edit
  const [error, setError] = useState('');
  const { showNotification } = useNotification();
  const { setCurrentProjectId } = useTaskContext();
  const guideService = useMemo(() => new GuideService(), []);
  const projectDefaultModel = useProjectDefaultAIModel(projectId);

  // Set current project ID in task context
  useEffect(() => {
    if (projectId) {
      setCurrentProjectId(projectId);
    }
  }, [projectId, setCurrentProjectId]);

  // State declarations
  const [openAudienceForm, setOpenAudienceForm] = useState(false);
  const [currentAudienceData, setCurrentAudienceData] = useState({ name: '', description: '', weight: 1 });
  const [editingAudienceId, setEditingAudienceId] = useState(null);

  // Set up Firestore snapshot listener for audiences
  useEffect(() => {
    if (!projectId) return;

    const audiencesRef = collection(db, `projects/${projectId}/audiences`);
    const q = query(audiencesRef, orderBy('creationDate', 'desc'));
    
    const unsubscribe = onSnapshot(q, () => {
      // const _fetchedAudiences = querySnapshot.docs.map(doc => ({
      //   id: doc.id,
      //   ...doc.data()
      // }));
      
      // Update the audiences through the parent component
      if (onAudiencesChange) {
        onAudiencesChange();
      }
    }, (error) => {
      console.error('Error listening to audiences:', error);
    });

    return () => unsubscribe();
  }, [projectId, onAudiencesChange]);

  // Handle audience parameter from URL on page load/refresh
  useEffect(() => {
    if (searchParams && setSearchParams && audiences.length > 0) {
      const audienceParam = searchParams.get('audience');
      if (audienceParam) {
        // Find the audience by ID
        const audienceToEdit = audiences.find(aud => aud.id === audienceParam);
        if (audienceToEdit && !openAudienceForm) {
          // Open the edit form for this audience
          setEditingAudienceId(audienceToEdit.id);
          setCurrentAudienceData({ 
            name: audienceToEdit.name, 
            description: audienceToEdit.description, 
            weight: audienceToEdit.weight || 1 
          });
          setOpenAudienceForm(true);
        }
      }
    }
  }, [audiences, searchParams, setSearchParams, openAudienceForm]);
  const [numberOfActors, setNumberOfActors] = useState(3); // Default to 3 actors, max 5 (B2C only — IGNORED in B2B mode where AI decides DMU pack size)
  // B2B mode: DMU-pack size is AI-decided based on product complexity + ICP buying triggers.
  // The "Actors per Audience" input is hidden; backend ignores `actorCount` for B2B audiences.
  const isB2B = project?.audienceModel === 'b2b';
  const [isDeletingSelected, setIsDeletingSelected] = useState(false); // For delete selected button
  const [isCreatingActors, setIsCreatingActors] = useState(false); // For actor creation loading state
  // Generate Actors model-selection dialog
  const [createActorsDialogOpen, setCreateActorsDialogOpen] = useState(false);
  const [actorGenerationModel, setActorGenerationModel] = useState(PROJECT_DEFAULT_AI_MODEL);
  const [actorGenerationModelTouched, setActorGenerationModelTouched] = useState(false);
  const [deletingAudienceId, setDeletingAudienceId] = useState(null); // For individual audience deletion
  const [, setAudiencesContainerWidth] = useState(0); // For individual audience deletion


  // New state for fixed toolbar
  const [toolbarNumberOfActors, setToolbarNumberOfActors] = useState(3); // Default to 3 actors

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuAudienceId, setMenuAudienceId] = useState(null);

  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const handleResize = () => {
    const el = document.getElementById("audience-management-view-container");
        if (el) {

          setAudiencesContainerWidth(el.offsetWidth);
        }
  };

  useEffect(() => {
      if(project) {
        handleResize();
        window.addEventListener("resize", handleResize);
      }
    }, [project]);

  useEffect(() => {
    if (!createActorsDialogOpen || actorGenerationModelTouched) return;
    setActorGenerationModel(projectDefaultModel.value);
  }, [actorGenerationModelTouched, createActorsDialogOpen, projectDefaultModel.value]);

  // Add auto-dismiss for success messages
  useEffect(() => {
    let timerId;
    if (error) {
      timerId = setTimeout(() => {
        setError('');
      }, 7000);
    }
    return () => clearTimeout(timerId);
  }, [error]);

  // Add logging to track selection state changes
  useEffect(() => {



  }, [selectedAudienceIds, controlledSelectedAudienceIds]);
  
  // Add logging to track audiences prop changes
  useEffect(() => {


  }, [audiences]);

  const handleSelectAudience = (audienceId) => {
    setSelectedAudienceIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(audienceId)) newSet.delete(audienceId);
      else newSet.add(audienceId);
      return newSet;
    });
  };

  // const _handleSelectAllAudiences = (event) => {
  //   if (event.target.checked) {
  //     const allIds = new Set(audiences.map(aud => aud.id));
  //     setSelectedAudienceIds(allIds);
  //   } else {
  //     setSelectedAudienceIds(new Set());
  //   }
  // };

  const handleOpenAudienceForm = (audienceToEdit = null) => {
    if (audienceToEdit) {
      setEditingAudienceId(audienceToEdit.id);
      setCurrentAudienceData({ name: audienceToEdit.name, description: audienceToEdit.description, weight: audienceToEdit.weight || 1 });
      
      // Update URL parameter to include audience ID
      if (searchParams && setSearchParams) {
        const currentParams = Object.fromEntries(searchParams.entries());
        setSearchParams({ ...currentParams, audience: audienceToEdit.id });
      }
    } else {
      setEditingAudienceId(null);
      setCurrentAudienceData({ name: '', description: '', weight: 1 });
      
      // Remove audience parameter from URL when creating new audience
      if (searchParams && setSearchParams) {
        const currentParams = Object.fromEntries(searchParams.entries());
        delete currentParams.audience;
        setSearchParams(currentParams);
      }
    }
    setError(''); // Clear form-specific error for dialog
    setOpenAudienceForm(true);
  };

  const handleCloseAudienceForm = () => {
    setOpenAudienceForm(false);
    setEditingAudienceId(null);
    setCurrentAudienceData({ name: '', description: '', weight: 1 });
    setError('');
    
    // Remove audience parameter from URL when closing the form
    if (searchParams && setSearchParams) {
      const currentParams = Object.fromEntries(searchParams.entries());
      delete currentParams.audience;
      setSearchParams(currentParams);
    }
  };

  const handleSaveAudience = async () => {
    if (!currentUser || !currentAudienceData.name.trim()) {
      setError('Audience name is required.');
      return;
    }
    setError('');
    setIsFormProcessing(true);
    try {
      if (editingAudienceId) {
        await updateDoc(doc(db, getProjectAudiencesPath(projectId), editingAudienceId), {
          name: currentAudienceData.name.trim(),
          description: currentAudienceData.description.trim(),
          weight: parseInt(currentAudienceData.weight) || 1,
          lastModified: serverTimestamp()
        });
        // showNotification('Audience updated successfully!');
      } else {
        await addDoc(collection(db, getProjectAudiencesPath(projectId)), {
          name: currentAudienceData.name.trim(),
          description: currentAudienceData.description.trim(),
          weight: parseInt(currentAudienceData.weight) || 1,
          creationDate: serverTimestamp()
        });
        // showNotification('Audience created successfully!');
      }
      handleCloseAudienceForm();
      if (onAudiencesChange) onAudiencesChange();
    } catch (err) {
      setError(`Failed to ${editingAudienceId ? 'update' : 'create'} audience: ${err.message}`);
      console.error(`Error ${editingAudienceId ? 'updating' : 'creating'} audience:`, err);
    }
    setIsFormProcessing(false);
  };

  const handleDeleteAudience = async (audienceId) => {
    if (!currentUser || !window.confirm("Are you sure you want to delete this audience and all its actors? This cannot be undone.")) return;
    setError(''); 
    setDeletingAudienceId(audienceId);
    try {
      await deleteAudienceWithActorsAndSubcollections(projectId, audienceId);
      if (onAudiencesChange) onAudiencesChange();
      setSelectedAudienceIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(audienceId);
        return newSet;
      });
      // showNotification('Audience deleted successfully!');
    } catch (err) {
      setError(`Failed to delete audience: ${err.message}`);
      console.error("Error deleting audience:", err);
    } finally {
      setDeletingAudienceId(null);
    }
  };

  const handleDeleteSelectedAudiences = async () => {
    if (selectedAudienceIds.size === 0 || !currentUser) {
      setError("No audiences selected for deletion.");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedAudienceIds.size} selected audience(s) and all their actors? This cannot be undone.`)) return;
    
    setIsDeletingSelected(true);
    setError('');
    let deletedCount = 0;
    const errors = [];

    for (const audienceId of selectedAudienceIds) {
      try {
        await deleteAudienceWithActorsAndSubcollections(projectId, audienceId);
        deletedCount++;
      } catch (err) {
        console.error(`Error deleting audience ${audienceId}:`, err);
        errors.push(`Failed to delete audience ${audienceId}: ${err.message}`);
      }
    }
    setIsDeletingSelected(false);
    if (onAudiencesChange) onAudiencesChange(); // Refresh the list
    setSelectedAudienceIds(new Set()); // Clear selection

    if (errors.length > 0) {
      setError(`Some errors occurred: ${errors.join('; ')}`);
    } else if (deletedCount > 0) {
      showNotification(`Successfully deleted ${deletedCount} audience(s)!`);
    }
  };

  const handleCreateActors = async () => {
    if (selectedAudienceIds.size === 0 || !currentUser) {
        setError("No audiences selected or user not identified.");
        return;
    }
    const numActorsVal = Number(numberOfActors);
    if (isNaN(numActorsVal) || numActorsVal <= 0) {
        setError("Number of actors to generate must be a positive integer.");
        return;
    }

    setError('');
    setIsCreatingActors(true);

    try {
      const selectedAudiencesDetails = buildActorGenerationAudienceRequests(
        audiences,
        selectedAudienceIds,
        numActorsVal,
      );

      if (selectedAudiencesDetails.length === 0) {
        setError("Could not find details for selected audiences.");
        setIsCreatingActors(false);
        return;
      }

      const agentApi = new AgentApiClient();
      const result = await agentApi.generateActors({
        projectId,
        userId: currentUser.uid,
        audiences: selectedAudiencesDetails,
        provider: getProviderForModel(actorGenerationModel),
        model: actorGenerationModel,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to start actor generation');
      }

      console.log('[AudienceManagement] Actor generation started, taskId:', result.taskId);
      setSelectedAudienceIds(new Set());
      showNotification('success');

      // Actors will appear via Firestore real-time listener when backend handler completes
      if (onActorsCreated) onActorsCreated([]);
      else if (fetchProjectData) fetchProjectData();

    } catch (err) {
      console.error(`Error during actor creation:`, err);
      setError(`Actor creation failed: ${err.message}`);
      if (fetchProjectData) fetchProjectData();
      setIsCreatingActors(false); // Clear loading state
    }
    
    setTimeout(() => {
      if (clearProjectTaskStatus) {
        clearProjectTaskStatus();
      }
    }, 5000);
    setIsCreatingActors(false); // Clear loading state
  };
  
  // Group audiences by task context
  const groupedAudiences = React.useMemo(() => {
    const audiencesWithTaskId = audiences.filter(
      (aud) => aud.taskId
    );
    const audiencesWithoutTaskId = audiences.filter(
      (aud) => !aud.taskId
    );

    const taskOrder = {}; // Para mapear taskId para { name, index, originalOrder }
    let taskNameCounts = {}; // Para contar ocorrências de cada taskName

    // Primeiro passe: coletar todas as tasks e seus nomes, e contar nomes
    audiencesWithTaskId.forEach(audience => {
      const taskId = audience.taskId;
      // Try to get task name from tasks prop first, then fallback to audience.taskName, then generate from taskId
      const task = tasks?.find(t => t.id === taskId);
      const taskName = task?.name || audience.taskName || `Task ${taskId}`;
      if (!taskOrder[taskId]) {
        // Mantemos uma ordem original baseada na primeira vez que vemos um taskId
        taskOrder[taskId] = { name: taskName, index: 0, originalOrder: Object.keys(taskOrder).length };
        taskNameCounts[taskName] = (taskNameCounts[taskName] || 0) + 1;
      }
    });

    // Segundo passe: atribuir índices apenas se o nome da task for duplicado
    const taskNameCurrentIndex = {};
    // Ordenar os taskIds pela ordem em que foram vistos para manter consistência
    const sortedTaskIds = Object.keys(taskOrder).sort((a, b) => taskOrder[a].originalOrder - taskOrder[b].originalOrder);

    sortedTaskIds.forEach(taskId => {
      const taskDetails = taskOrder[taskId];
      if (taskNameCounts[taskDetails.name] > 1) { // Só adicionar índice se o nome for duplicado
        taskNameCurrentIndex[taskDetails.name] = (taskNameCurrentIndex[taskDetails.name] || 0) + 1;
        taskDetails.index = taskNameCurrentIndex[taskDetails.name];
      }
    });

    const groupedData = {};

    // Agrupar "No Specific Task"
    if (audiencesWithoutTaskId.length > 0) {
      groupedData["No Specific Task"] = audiencesWithoutTaskId;
    }

    // Agrupar por task com o novo groupKey
    audiencesWithTaskId.forEach(audience => {
      const taskId = audience.taskId;
      // Try to get task name from tasks prop first, then fallback to audience.taskName, then generate from taskId
      const task = tasks?.find(t => t.id === taskId);
      const taskName = task?.name || audience.taskName || `Task ${taskId}`;
      let groupKey = taskName;
      // Adicionar índice ao groupKey apenas se taskDetails.index for > 0
      if (taskOrder[taskId] && taskOrder[taskId].index > 0) {
        groupKey = `${taskName} (${taskOrder[taskId].index})`;
      }
      
      if (!groupedData[groupKey]) {
        groupedData[groupKey] = [];
      }
      groupedData[groupKey].push(audience);
    });
    
    return groupedData;
  }, [audiences, tasks]);

  // const _isActorsTaskRunning = project?.taskStatus === 'running' && project?.taskType === 'actors';
  // Removed loading state restrictions - buttons are now always available
  // const _isGlobalTaskRunning = false;

  const handleMenuOpen = (event, audienceId) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuAudienceId(audienceId);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuAudienceId(null);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Helper to sort audiences for each group
  const sortAudiences = (audiencesArr) => {
    const sorted = [...audiencesArr];
    sorted.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      if (sortBy === 'creationDate') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      } else {
        aValue = aValue || '';
        bValue = bValue || '';
      }
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };


  return (
    <Paper
      id='audience-management-view-container'
      elevation={0}
      sx={{ p: {xs: 1, sm: 2, md: 3}, mt: 0, pb: '80px' }}
      style={{
        height: '100%',
        overflow: 'hidden',
        overflowY: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" component="h3" gutterBottom={audiences.length === 0}>
          Audiences
        </Typography>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
          {/* {audiences.length > 0 && (
            <Button 
              variant="outlined" 
              color="error" 
              size="small" 
              startIcon={isDeletingSelected ? <CircularProgress size={20} color="inherit" /> : <DeleteSweepIcon />} 
              onClick={handleDeleteSelectedAudiences}
              disabled={isDeletingSelected || selectedAudienceIds.size === 0}
            >
              {isDeletingSelected ? 'Deleting...' : `Delete Selected (${selectedAudienceIds.size})`}
            </Button>
          )} */}
          <WithGuideHelp
            guideKey="add-audience-button"
            guideService={guideService}
            position="top-right"
          >
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleOpenAudienceForm()} disabled={false}>
              Add Audience
            </Button>
          </WithGuideHelp>
        </Box>
      </Box>
      {error && !openAudienceForm && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      <Dialog
        open={openAudienceForm}
        onClose={handleCloseAudienceForm}
        maxWidth="md"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
            padding: '0 0 8px',
          }}
        >
          {editingAudienceId ? 'Edit Audience' : 'Add New Audience'}
          <IconButton
            onClick={handleCloseAudienceForm}
            size="small"
            sx={{
              backgroundColor: (theme) => theme.palette.action.hover,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: '0',
            my: 2,
          }}
        >
          {error && openAudienceForm && <Typography color="error" sx={{ mb:1, fontSize: '0.9rem' }}>{error}</Typography>}
          <TextField autoFocus margin="dense" id="audienceFormName" label="Audience Name" type="text" fullWidth variant="outlined"
            value={currentAudienceData.name} onChange={(e) => setCurrentAudienceData({...currentAudienceData, name: e.target.value})} required sx={{ mb: 2 }}
            onKeyPress={(e) => e.key === 'Enter' && handleSaveAudience()}
          />
          <TextField
            margin="dense"
            id="audienceFormDescription"
            label="Audience Description"
            type="text"
            fullWidth
            multiline
            variant="outlined"
            value={currentAudienceData.description} onChange={(e) => setCurrentAudienceData({...currentAudienceData, description: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField margin="dense" id="audienceFormWeight" label="Weight" type="number" fullWidth variant="outlined"
            value={currentAudienceData.weight} onChange={(e) => setCurrentAudienceData({...currentAudienceData, weight: e.target.value})}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: '16px 0 0' }}>
          <Button onClick={handleSaveAudience} variant="contained" disabled={isFormProcessing}>
            {isFormProcessing ? <CircularProgress size={18} color="inherit"/> : (editingAudienceId ? 'Save Changes' : 'Create Audience')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Actors model-selection dialog */}
      <Dialog
        open={createActorsDialogOpen}
        onClose={() => !isCreatingActors && setCreateActorsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Actors</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {isB2B
                ? <>Generating <strong>DMU pack</strong> per ICP archetype for {selectedAudienceIds.size} selected {selectedAudienceIds.size === 1 ? 'archetype' : 'archetypes'}. Pack size (typically 2-6 stakeholders) is decided by AI based on product complexity and the ICP&apos;s buying triggers.</>
                : <>Generating <strong>{toolbarNumberOfActors}</strong> {toolbarNumberOfActors === 1 ? 'actor' : 'actors'} per audience for {selectedAudienceIds.size} selected {selectedAudienceIds.size === 1 ? 'audience' : 'audiences'}.</>
              }
            </Typography>
            {!isB2B && (
              <TextField
                label="Actors per Audience"
                type="number"
                value={toolbarNumberOfActors}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (val > 0 && val <= 5) {
                    setToolbarNumberOfActors(val);
                    setNumberOfActors(val);
                  } else if (e.target.value === '') {
                    setToolbarNumberOfActors('');
                    setNumberOfActors(1);
                  }
                }}
                inputProps={{ min: 1, max: 5 }}
                size="small"
                fullWidth
              />
            )}
            <AvailableModelSelector
              projectId={projectId}
              value={actorGenerationModel}
              onChange={(model) => {
                setActorGenerationModelTouched(true);
                setActorGenerationModel(model);
              }}
              label="AI Model"
              size="small"
              fullWidth
              disabled={isCreatingActors}
              testId="actor-generation-model-selector"
            />
            <Typography variant="caption" color="text.secondary">
              Uses the project default model unless you choose another one for this run.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: '8px 24px 16px' }}>
          <Button onClick={() => setCreateActorsDialogOpen(false)} disabled={isCreatingActors}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={selectedAudienceIds.size === 0 || toolbarNumberOfActors === '' || toolbarNumberOfActors <= 0 || isCreatingActors}
            onClick={async () => {
              setCreateActorsDialogOpen(false);
              await handleCreateActors();
            }}
            startIcon={isCreatingActors ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
          >
            {isCreatingActors ? 'Creating Actors...' : 'Create Actors'}
          </Button>
        </DialogActions>
      </Dialog>

      {audiences.length === 0 && (
        <Typography sx={{ textAlign: 'center', mt: 3, color: 'text.secondary' }}>
          No audiences yet. Add one manually or generate them from the Product Description tab.
        </Typography>
      )}
      
      {audiences.length > 0 && (
        <>
          {/* Render audiences grouped by task */}
          {Object.entries(groupedAudiences).map(([groupKey, taskAudiences]) => {
            if (taskAudiences.length === 0) return null;
            
            return (
              <Box key={groupKey} sx={{ mb: 4,
                pb: {
                  xs: "245px",
                  sm: "200px",
                  md: "135px",
                  lg: "50px",
                },
               }}
              >
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                  {groupKey}
                </Typography>
                  
                <TableContainer component={Paper} variant="outlined" sx={{mt: 2, overflowX: 'auto'}}>
                  <Table size="small" sx={{minWidth: 650}}>
                    <TableHead>
                      <TableRow> 
                        <TableCell padding="checkbox" sx={{ verticalAlign: 'middle', textAlign: 'center', width: 48 }}>
                          <Checkbox
                            checked={taskAudiences.length > 0 && taskAudiences.every(aud => selectedAudienceIds.has(aud.id))}
                            indeterminate={taskAudiences.some(aud => selectedAudienceIds.has(aud.id)) && !taskAudiences.every(aud => selectedAudienceIds.has(aud.id))}
                            onChange={e => {
                              if (e.target.checked) {
                                setSelectedAudienceIds(prev => {
                                  const newSet = new Set(prev);
                                  taskAudiences.forEach(aud => newSet.add(aud.id));
                                  return newSet;
                                });
                              } else {
                                setSelectedAudienceIds(prev => {
                                  const newSet = new Set(prev);
                                  taskAudiences.forEach(aud => newSet.delete(aud.id));
                                  return newSet;
                                });
                              }
                            }}
                            disabled={taskAudiences.length === 0}
                            size="small"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell sx={{minWidth: 150, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                          <TableSortLabel
                            active={sortBy === 'name'}
                            direction={sortBy === 'name' ? sortOrder : 'asc'}
                            onClick={() => handleSort('name')}
                          >
                            Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{minWidth: 250, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                          <TableSortLabel
                            active={sortBy === 'description'}
                            direction={sortBy === 'description' ? sortOrder : 'asc'}
                            onClick={() => handleSort('description')}
                          >
                            Description
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{minWidth: 260, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                          Acquisition
                        </TableCell>
                        {/*<TableCell sx={{minWidth: 100, whiteSpace: 'nowrap'}}>*/}
                        {/*  <TableSortLabel*/}
                        {/*    active={sortBy === 'weight'}*/}
                        {/*    direction={sortBy === 'weight' ? sortOrder : 'asc'}*/}
                        {/*    onClick={() => handleSort('weight')}*/}
                        {/*  >*/}
                        {/*    Weight*/}
                        {/*  </TableSortLabel>*/}
                        {/*</TableCell>*/}
                        <TableCell sx={{minWidth: 150, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                          <TableSortLabel
                            active={sortBy === 'creationDate'}
                            direction={sortBy === 'creationDate' ? sortOrder : 'asc'}
                            onClick={() => handleSort('creationDate')}
                          >
                            Created
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sx={{minWidth: 120, whiteSpace: 'normal', wordBreak: 'break-word'}}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortAudiences(taskAudiences).map((audience) => (
                        <TableRow
                          hover
                          key={audience.id}
                          selected={selectedAudienceIds.has(audience.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={selectedAudienceIds.has(audience.id)}
                              onChange={() => handleSelectAudience(audience.id)}
                              disabled={false}
                              size="small"
                              onClick={e => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell component="th" scope="row" sx={{whiteSpace: 'normal', wordBreak: 'break-word'}} onClick={() => handleOpenAudienceForm(audience)} style={{ cursor: 'pointer' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                              <span>{audience.name}</span>
                              {audience.dmuMode === 'b2b' ? (
                                <Chip
                                  label="ICP"
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  title="B2B: ICP archetype with DMU pack"
                                  sx={{ fontSize: '0.65rem', height: 18 }}
                                />
                              ) : null}
                            </Box>
                            {audience.dmuMode === 'b2b' && audience.icpProfile && typeof audience.icpProfile === 'object' ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {audience.icpProfile.industry ? (
                                  <Chip label={String(audience.icpProfile.industry)} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                                ) : null}
                                {audience.icpProfile.companySize ? (
                                  <Chip label={String(audience.icpProfile.companySize)} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                                ) : null}
                                {audience.icpProfile.regulation ? (
                                  <Chip label={Array.isArray(audience.icpProfile.regulation) ? audience.icpProfile.regulation.join(', ') : String(audience.icpProfile.regulation)} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                                ) : null}
                                {audience.icpProfile.maturityStage ? (
                                  <Chip label={String(audience.icpProfile.maturityStage)} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                                ) : null}
                              </Box>
                            ) : null}
                          </TableCell>
                          <TableCell
                            sx={{
                              maxWidth: '40px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            onClick={() => handleOpenAudienceForm(audience)}
                            style={{ cursor: 'pointer' }}
                          >
                            {audience.description}
                          </TableCell>
                          <TableCell
                            sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                            onClick={() => handleOpenAudienceForm(audience)}
                            style={{ cursor: 'pointer' }}
                          >
                            <AudienceAcquisitionChannelsCell audience={audience} />
                          </TableCell>
                          {/*<TableCell sx={{whiteSpace: 'nowrap'}} onClick={() => handleOpenAudienceForm(audience)} style={{ cursor: 'pointer' }}>{audience.weight || 1}</TableCell>*/}
                          <TableCell sx={{whiteSpace: 'normal', wordBreak: 'break-word'}} onClick={() => handleOpenAudienceForm(audience)} style={{ cursor: 'pointer' }}>{formatTimestamp(audience.creationDate)}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={e => handleMenuOpen(e, audience.id)}
                              aria-label="actions"
                              aria-controls={menuAudienceId === audience.id ? `audience-menu-${audience.id}` : undefined}
                              aria-haspopup="true"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                            <Menu
                              id={`audience-menu-${audience.id}`}
                              anchorEl={menuAnchorEl}
                              open={Boolean(menuAnchorEl) && menuAudienceId === audience.id}
                              onClose={handleMenuClose}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                              PaperProps={{ sx: { minWidth: 160 } }}
                            >
                              <MenuItem onClick={() => { handleOpenAudienceForm(audience); handleMenuClose(); }}>Edit</MenuItem>
                              <MenuItem 
                                onClick={() => { handleDeleteAudience(audience.id); handleMenuClose(); }}
                                disabled={deletingAudienceId === audience.id}
                              >
                                {deletingAudienceId === audience.id ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    Deleting...
                                  </Box>
                                ) : (
                                  'Delete'
                                )}
                              </MenuItem>
                            </Menu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}
        </>
      )}

      {/* Fixed toolbar for actor generation */}
      {audiences.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            mt: 2,
            p: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            gap: { xs: 1, sm: 1.5 },
            flexWrap: 'wrap',
            zIndex: 1,
            background: 'none',
            backdropFilter: 'blur(34px)',
            boxShadow: 'none',
          }}
        >
          {error && <Alert severity="error" sx={{ mb: 2, flex: '1 1 100%' }} onClose={() => setError('')}>{error}</Alert>}
          
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {!isB2B && (
                <TextField
                  label="Actors per Audience"
                  type="number"
                  value={toolbarNumberOfActors}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (val > 0 && val <= 5) {
                      setToolbarNumberOfActors(val);
                      setNumberOfActors(val);
                    } else if (e.target.value === '') {
                      setToolbarNumberOfActors('');
                      setNumberOfActors(1);
                    }
                  }}
                  inputProps={{ min: 1, max: 5 }}
                  sx={{ width: '135px' }}
                  variant="outlined"
                  size="small"
                />
              )}
              <Button
                variant="contained"
                size="small"
                color="primary"
                startIcon={isCreatingActors ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                onClick={() => {
                  setActorGenerationModelTouched(false);
                  setActorGenerationModel(projectDefaultModel.value);
                  setCreateActorsDialogOpen(true);
                }}
                disabled={selectedAudienceIds.size === 0 || toolbarNumberOfActors === '' || toolbarNumberOfActors <= 0 || isCreatingActors}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {isCreatingActors ? 'Creating Actors...' : `Create Actors`}
              </Button>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={handleDeleteSelectedAudiences}
                disabled={selectedAudienceIds.size === 0 || isDeletingSelected}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
    </Paper>
  );
}

AudienceManagementSection.propTypes = {
  project: PropTypes.object.isRequired,
  projectId: PropTypes.string.isRequired,
  audiences: PropTypes.array.isRequired,
  onAudiencesChange: PropTypes.func,
  onActorsCreated: PropTypes.func,
  currentUser: PropTypes.object.isRequired,
  fetchProjectData: PropTypes.func,
  clearProjectTaskStatus: PropTypes.func,
  selectedAudienceIds: PropTypes.object,
  setSelectedAudienceIds: PropTypes.func,
  tasks: PropTypes.array,
  searchParams: PropTypes.object,
  setSearchParams: PropTypes.func,
};

export default AudienceManagementSection;
